/**
 * Column sorting shared by both tracker boards. A board holds a single
 * `ColumnSort` ({columnId, dir}); clicking a column header cycles it
 * asc → desc → off via `cycleColumnSort`, and the board orders its rows with
 * `compareByColumn`.
 *
 * Empty values always sort LAST (in both directions) so a sort never buries
 * populated rows under blanks. Only categorical/ordinal column types are
 * sortable (see `isSortableColumnType`); people/project/brief-type/assets are
 * better served by grouping.
 */
import type { CustomColumnType } from './useTrackersBoard';

export type SortDir = 'asc' | 'desc';
export interface ColumnSort {
  columnId: string;
  dir: SortDir;
}

const SORTABLE_TYPES = new Set<CustomColumnType>([
  'text',
  'number',
  'date',
  'date_range',
  'status',
  'checkbox',
  'link',
  'progress',
  'team',
]);

export const isSortableColumnType = (type: CustomColumnType | undefined): boolean =>
  type ? SORTABLE_TYPES.has(type) : false;

/** Marks a toolbar sort id as naming a column; the remainder is the column id. */
// eslint-disable-next-line i18next/no-literal-string -- an id namespace, never rendered
export const COLUMN_SORT_PREFIX = 'column:';

/** Header-click cycle for a column: asc → desc → cleared. */
export function cycleColumnSort(current: ColumnSort | null, columnId: string): ColumnSort | null {
  if (!current || current.columnId !== columnId) return { columnId, dir: 'asc' };
  return current.dir === 'asc' ? { columnId, dir: 'desc' } : null;
}

interface SortableColumn {
  id: string;
  type?: CustomColumnType;
  statusOptions?: ReadonlyArray<{ id: string }>;
}
interface HasCustomValues {
  customColumnValues?: Record<string, unknown> | null;
}

/**
 * Comparator for one column + direction. Dates compare lexically (ISO
 * `YYYY-MM-DD` — and date-range `start` — sort chronologically as strings);
 * status by its configured option order; checkbox unchecked < checked;
 * everything else by locale-aware string. `null` rank = empty → always last.
 */
export function compareByColumn<T extends HasCustomValues>(
  column: SortableColumn,
  dir: SortDir,
): (a: T, b: T) => number {
  const sign = dir === 'asc' ? 1 : -1;
  const statusOrder = new Map((column.statusOptions ?? []).map((o, i) => [o.id, i] as const));

  const rank = (item: T): number | string | null => {
    const raw = item.customColumnValues?.[column.id];
    switch (column.type) {
      case 'number':
      case 'progress':
        return typeof raw === 'number' ? raw : null;
      case 'checkbox':
        // Always present: absent/false = unchecked (0), true = checked (1).
        return raw === true ? 1 : 0;
      case 'status':
        return typeof raw === 'string' && statusOrder.has(raw)
          ? (statusOrder.get(raw) ?? null)
          : null;
      case 'date_range': {
        const start =
          raw && typeof raw === 'object' ? (raw as { start?: string }).start : undefined;
        return start && start.length > 0 ? start : null;
      }
      default:
        // text / date / link / team — string (or a stray number) if non-empty.
        if (typeof raw === 'string') return raw.length > 0 ? raw : null;
        return typeof raw === 'number' ? raw : null;
    }
  };

  return (a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    const ea = ra === null;
    const eb = rb === null;
    if (ea && eb) return 0;
    if (ea) return 1; // empties last, ignoring dir
    if (eb) return -1;
    const cmp =
      typeof ra === 'number' && typeof rb === 'number'
        ? ra - rb
        : String(ra).localeCompare(String(rb));
    return cmp * sign;
  };
}

interface SortableRow extends HasCustomValues {
  title: string;
  createdAt: string;
}

/**
 * Comparator for a toolbar sort id, or `null` when there is nothing to apply
 * and the caller should fall back to the user's drag order.
 *
 * Two id families: fixed `field-dir` pairs for the fields every row has, and
 * `column:<id>`, which hands off to that column's own comparator so picking a
 * column in the toolbar and clicking its header order the rows identically.
 * A `column:` id whose column has since been deleted resolves to `null`
 * rather than an arbitrary order.
 */
export function compareBySortId<T extends SortableRow>(
  sortId: string | null,
  columns: ReadonlyArray<SortableColumn> = [],
): ((a: T, b: T) => number) | null {
  if (!sortId) return null;

  if (sortId.startsWith(COLUMN_SORT_PREFIX)) {
    const columnId = sortId.slice(COLUMN_SORT_PREFIX.length);
    const column = columns.find((c) => c.id === columnId);
    return column ? compareByColumn<T>(column, 'asc') : null;
  }

  switch (sortId) {
    case 'createdAt-desc':
      return (a, b) => +new Date(b.createdAt) - +new Date(a.createdAt);
    case 'createdAt-asc':
      return (a, b) => +new Date(a.createdAt) - +new Date(b.createdAt);
    case 'title-asc':
      return (a, b) => a.title.localeCompare(b.title);
    case 'title-desc':
      return (a, b) => b.title.localeCompare(a.title);
    default:
      return null;
  }
}
